import type { AppState } from '../core/types';
import { activeArea } from '../domain/classification';
import { SECONDARY_NO_VALUE } from '../domain/dualClassification';
import { entityViewEntities } from '../domain/entityView';
import type { ScenarioAssignments } from '../domain/scenarios';
import { secondaryTheme, type SecondaryTheme } from './dualTheme';
import { masterThemeAssignments, type MasterTheme } from './masterTheme';

/**
 * V1.9.0 E3 · cross of the two classifications for the grouped legend and the scenario table: secondary (B, e.g.
 * Hacienda) → principal (A, e.g. tipo de riego) → active ha and unique entities.
 *
 * It reads the FINAL pieces the map draws (masterThemeAssignments: Áreas parciales, a proposed Tablón, a scenario) and
 * B per entity (secondaryTheme): no second engine. An entity split into pieces counts once in every category where it
 * has area, but only once in a group or a total of unique entities; areas are never added twice.
 */
export interface CrossCell { readonly areaHa: number; readonly entities: ReadonlySet<string> }
export interface CrossGroup extends CrossCell { readonly categories: ReadonlyMap<string, CrossCell> }
export interface CrossTab { readonly groups: ReadonlyMap<string, CrossGroup>; readonly total: CrossCell }

/** 'shown' = what the map draws (the proposal when one is drawn); 'actual' = the classification as it is. */
export type CrossSide = 'shown' | 'actual';

const EPSILON_HA = 1e-9;

/** Active ha by category of every classified entity, from the same parts the map, legend and indicators use. */
function entityCategoryAreas(state: AppState, theme: MasterTheme, side: CrossSide): ReadonlyMap<string, ReadonlyMap<string, number>> {
  const model = state.model!; const result = new Map<string, Map<string, number>>();
  const add = (entity: string, category: string, area: number) => {
    if (area <= EPSILON_HA) return;
    const map = result.get(entity) ?? new Map<string, number>(); map.set(category, (map.get(category) ?? 0) + area); result.set(entity, map);
  };
  const flows = (theme.assignments as Partial<ScenarioAssignments>).flows;
  if (flows) { flows.forEach((flow) => add(flow.entityId, side === 'actual' ? flow.from : flow.to, flow.areaHa)); return result; }
  const level = theme.mixed ? 'suerte' : state.entityViewLevel;
  entityViewEntities(model, level, theme.subsetIds).forEach((row) => {
    const pieces = theme.piecesBySuerteId.get(row.id);
    if (pieces) pieces.forEach((piece) => add(row.id, piece.category, piece.areaHa));
    else add(row.id, theme.assignments.categoryByEntityId.get(row.id) ?? 'Sin valor', activeArea(model, row.fragmentIds));
  });
  return result;
}

const memo = new Map<CrossSide, { readonly key: readonly unknown[]; readonly cross: CrossTab }>();

/** The cross (null without an active secondary classification on the Maestro). Memoized per theme, B and side. */
export function legendCrossTab(state: AppState, side: CrossSide = 'shown'): CrossTab | null {
  const layer = state.layers.find(({ id, kind }) => kind === 'master' && id === state.classificationConfig.layerId);
  const dual = secondaryTheme(state); const theme = layer ? masterThemeAssignments(state, layer) : null;
  if (!state.model || !dual || !theme) return null;
  const key = [state.model, theme.assignments, theme.piecesBySuerteId, theme.subsetIds, dual.assignments, state.entityViewLevel];
  const hit = memo.get(side); if (hit && hit.key.every((value, index) => value === key[index])) return hit.cross;
  const cross = computeCross(state, theme, dual, side);
  memo.set(side, { key, cross });
  return cross;
}

function computeCross(state: AppState, theme: MasterTheme, dual: SecondaryTheme, side: CrossSide): CrossTab {
  const groups = new Map<string, { areaHa: number; entities: Set<string>; categories: Map<string, { areaHa: number; entities: Set<string> }> }>();
  const totalEntities = new Set<string>(); let totalHa = 0;
  entityCategoryAreas(state, theme, side).forEach((areas, entity) => {
    const value = dual.assignments.categoryByEntityId.get(entity) ?? SECONDARY_NO_VALUE;
    const group = groups.get(value) ?? { areaHa: 0, entities: new Set<string>(), categories: new Map() };
    areas.forEach((area, category) => {
      const cell = group.categories.get(category) ?? { areaHa: 0, entities: new Set<string>() };
      cell.areaHa += area; cell.entities.add(entity); group.categories.set(category, cell);
      group.areaHa += area; totalHa += area;
    });
    group.entities.add(entity); totalEntities.add(entity); groups.set(value, group);
  });
  return { groups, total: { areaHa: totalHa, entities: totalEntities } };
}
